import { zoneOffsetMinutes } from './ntp.js';

/**
 * The platform's clock. Every time this platform prints, it prints in East
 * Africa Time.
 *
 * Operator, 2026-10-05: "set the timezone to nairobi across the platform,
 * everywhere, those timestamps are wrong". Before this a time was printed in
 * whatever zone the thing printing it was in: the BROWSER's (a laptop on UTC
 * read every event three hours early), UTC on the billing pages' payment
 * stamps, the phone's on the mobile app, and the server's own in API
 * sentences. Named once here, the web, the API, the mobile app and the MCP
 * server all print the same wall clock for the same instant.
 *
 * Instants only. A calendar DATE that is not an instant keeps the zone it was
 * cut in: a billing period runs between UTC month starts, so its labels stay
 * UTC (`monthDate` in the web's billing/common.ts) — rendered here, the last
 * day of August would print as 1 September.
 */
export const PLATFORM_TIME_ZONE = 'Africa/Nairobi';

/** What the platform zone is called in a sentence, e.g. "20:25 EAT". */
export const PLATFORM_TIME_ZONE_ABBREVIATION = 'EAT';

/**
 * Nairobi has kept UTC+3 without daylight saving since 1960. Used only where
 * the runtime has no tzdata for the zone (a Hermes build without full Intl on
 * the mobile app), so a phone still prints Nairobi time rather than its own.
 */
const FALLBACK_OFFSET_MINUTES = 180;

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTHS_LONG = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Anything a time arrives as: epoch ms, an ISO string (GenieACS), or a Date. */
export type TimeInput = number | string | Date | null | undefined;

function toMs(value: TimeInput): number | null {
  if (value === null || value === undefined || value === '') return null;
  const ms = value instanceof Date ? value.getTime() : typeof value === 'number' ? value : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

export interface WallClock {
  year: number;
  /** 1-12. */
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
  /** 0 = Sunday. */
  weekday: number;
}

/** The platform zone's offset from UTC at `at`, in minutes (+180 for Nairobi). */
export function platformOffsetMinutes(at: number = Date.now()): number {
  return zoneOffsetMinutes(PLATFORM_TIME_ZONE, at) ?? FALLBACK_OFFSET_MINUTES;
}

/**
 * The wall clock in the platform zone at an instant. Arithmetic on the offset,
 * then UTC getters — never the runtime's local getters, which answer in the
 * zone of whatever machine runs this.
 */
export function platformWallClock(value: TimeInput): WallClock | null {
  const ms = toMs(value);
  if (ms === null) return null;
  const d = new Date(ms + platformOffsetMinutes(ms) * 60_000);
  return {
    year: d.getUTCFullYear(),
    month: d.getUTCMonth() + 1,
    day: d.getUTCDate(),
    hour: d.getUTCHours(),
    minute: d.getUTCMinutes(),
    second: d.getUTCSeconds(),
    weekday: d.getUTCDay(),
  };
}

const two = (n: number): string => String(n).padStart(2, '0');

/** `20:25`, or `20:25:16` with seconds. `—` when there is no time. */
export function formatTime(value: TimeInput, options: { seconds?: boolean } = {}): string {
  const w = platformWallClock(value);
  if (!w) return '—';
  const base = `${two(w.hour)}:${two(w.minute)}`;
  return options.seconds ? `${base}:${two(w.second)}` : base;
}

/** `5 Oct 2026`. */
export function formatDate(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '—';
  return `${w.day} ${MONTHS_SHORT[w.month - 1]} ${w.year}`;
}

/** `5 Oct` — a chart axis or a short list, where the year is obvious. */
export function formatDayMonth(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '—';
  return `${w.day} ${MONTHS_SHORT[w.month - 1]}`;
}

/** `5 Oct 2026, 20:25` — or `…, 20:25:16` with seconds. */
export function formatDateTime(value: TimeInput, options: { seconds?: boolean } = {}): string {
  const ms = toMs(value);
  if (ms === null) return '—';
  return `${formatDate(ms)}, ${formatTime(ms, options)}`;
}

/**
 * `20:25:16 - 5/October/2026` — the event-row stamp. Seconds are kept because
 * ONU drops on one PON port land within the same minute.
 */
export function formatEventTime(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '—';
  return `${two(w.hour)}:${two(w.minute)}:${two(w.second)} - ${w.day}/${MONTHS_LONG[w.month - 1]}/${w.year}`;
}

/**
 * `5 Oct 2026, 20:25:16 EAT` — an instant inside a sentence the API writes.
 * The zone is named because a sentence travels: into the audit log, a
 * toast, a report row, a message an operator pastes to someone else.
 */
export function formatInstant(value: TimeInput): string {
  const ms = toMs(value);
  if (ms === null) return 'an unknown time';
  return `${formatDateTime(ms, { seconds: true })} ${PLATFORM_TIME_ZONE_ABBREVIATION}`;
}

/** `5 October 2026` — a date in a letter (mail, receipts). */
export function formatLongDate(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '—';
  return `${w.day} ${MONTHS_LONG[w.month - 1]} ${w.year}`;
}

/** `2026-10-05` — the platform-zone date, for a file name or a date field. */
export function platformIsoDate(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '';
  return `${w.year}-${two(w.month)}-${two(w.day)}`;
}

/** `2026-10-05 20:25:16` — sortable, for logs and exports. */
export function formatSortable(value: TimeInput): string {
  const w = platformWallClock(value);
  if (!w) return '';
  return `${platformIsoDate(value)} ${two(w.hour)}:${two(w.minute)}:${two(w.second)}`;
}
